import { FC, useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import type {
  GranteeForReview,
  GrantItemForReview,
} from '../../../../documents/templates/components/forms/map-fields-modal/review-documents-modal';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { ReviewDocumentsModal } from '../../../../documents/templates/components/forms/map-fields-modal/review-documents-modal';
import { useTemplateById } from '../../../../documents/templates/hooks/use-template-by-id.hook';
import { DocumentTemplateCombobox } from '../../components/index';
import { ChildrenFormProps } from '../../event-form.types';

export const BulkGrantStepThird: FC<ChildrenFormProps> = ({
  control,
  handleCloseModal,
  nextFormStep,
  prevFormStep,
  setFormData,
}) => {
  const selectedTemplateId = useWatch({
    control,
    name: 'stepOne.eventDetails.grant.selectedTemplateId',
  });
  const sharePlan = useWatch({ control, name: 'stepOne.eventDetails.grant.sharePlan' });
  const grantItems =
    useWatch({
      control,
      name: 'stepOne.eventDetails.grant.grantItems',
      defaultValue: [],
    }) ?? [];
  const hasSelectedTemplate = Boolean(selectedTemplateId);

  const { template } = useTemplateById(selectedTemplateId ?? null);
  const hasTemplatePlanMismatch = Boolean(
    selectedTemplateId &&
      template?.equityPlan?.id &&
      sharePlan?.id &&
      template.equityPlan.id !== sharePlan.id,
  );

  useEffect(() => {
    if (!hasTemplatePlanMismatch) return;
    setFormData('stepOne.eventDetails.grant.selectedTemplateId', undefined);
    setFormData('stepOne.eventDetails.grant.signatoryAssignments', {});
  }, [hasTemplatePlanMismatch, setFormData]);

  const granteesForReview: GranteeForReview[] = grantItems.map((item) => ({
    email: item.stakeholder?.email,
    fullName: item.stakeholder?.fullName ?? '',
    grantsCount: Number(item.shares) || 0,
    id: item.stakeholder?.id ?? '',
  }));

  const grantItemsForReview: GrantItemForReview[] = grantItems.map((item) => ({
    grantDate: item.grantDate,
    sharePlan: item.sharePlan ? { id: item.sharePlan.id } : undefined,
    shares: Number(item.shares) || 0,
    stakeholder: { id: item.stakeholder?.id ?? '' },
  }));

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-6 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            <AppFormattedMessage id="DOCUMENT_TEMPLATE" />
          </label>
          <DocumentTemplateCombobox control={control} />
        </div>

        {hasSelectedTemplate && !hasTemplatePlanMismatch && (
          <div className="rounded-md bg-brand-25 p-[2px]">
            <div className="flex flex-col rounded-md bg-white shadow-[0px_1px_2px_0px_rgba(16,24,40,0.06)]">
              <div className="flex h-12 items-center justify-between gap-2 border-b border-gray-100 px-4">
                <span className="text-sm text-gray-500">
                  <AppFormattedMessage id="STATUS" />
                </span>
                <span className="rounded-3xl bg-forest-50 px-2 py-1 text-xs font-medium text-forest-600">
                  <AppFormattedMessage id="DOCUMENTS_CREATED" />
                </span>
              </div>
              <div className="flex h-12 items-center justify-between gap-2 border-b border-gray-100 px-4">
                <span className="text-sm text-gray-500">
                  <AppFormattedMessage id="EQUITY_PLAN" />
                </span>
                <span className="text-sm font-medium text-gray-700">{sharePlan?.name ?? '—'}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            onClick={nextFormStep}
            type="button"
          >
            <AppFormattedMessage id="NEXT" />
          </Button>
        </div>
      </div>
      <ReviewDocumentsModal
        grantees={granteesForReview}
        grantItems={grantItemsForReview}
        template={template}
      />
    </>
  );
};
